import { FunctionComponent, useContext, useEffect, useState } from "react";
import XMarkIcon from "@heroicons/react/24/outline/XMarkIcon";
import IconButton from "../icon-button";
import { supabase, uploadFile } from "libs/supabase";
import { LoadingContext } from "providers/loading";
import { Asset, AssetType } from "types/asset";
import { useId } from "react-id-generator";
import classNames from "classnames";
import PhotoTaker from "../dashboard/photo-taker";
import UploadButton from "components/dashboard/upload-button";
import { ASSET_CARD_TEST_ID } from "constants/test";

interface AssetPickerProps {
  assetType: AssetType;
  onPick: (asset: Asset) => void;
  onClose: () => void;
}

const AssetPicker: FunctionComponent<AssetPickerProps> = ({ assetType, onPick, onClose }) => {
  const { setLoading } = useContext(LoadingContext);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const ids = useId(assets.length);
  const [modelUrl, setModelUrl] = useState<string>();

  useEffect(() => {
    supabase
      .from("assets")
      .select("*")
      .eq("type", assetType)
      .then((res) => {
        setLoading(false);
        if ("data" in res) {
          setAssets(res.data as Asset[]);
        }
      });
  }, [refreshKey]);

  return (
    <div className="fixed left-0 top-0 w-full h-full flex justify-end z-50 bg-black/20">
      {!modelUrl ? null : (
        <PhotoTaker
          onPhoto={async (file) => {
            try {
              setLoading(true);
              const image_url = await uploadFile("images", file);
              const url = modelUrl;
              setModelUrl(undefined);
              if (image_url) {
                const newAsset: Asset = { image_url, url, type: assetType };
                await supabase.from("assets").insert(newAsset);
              }
              setLoading(false);
              setRefreshKey((prev) => prev + 1);
            } catch (er) {
              console.log(er);
            }
          }}
          type={assetType}
          onClose={() => setModelUrl(undefined)}
          modelUrl={modelUrl}
        />
      )}
      <div className="bg-white p-7 max-w-[893px] h-full overflow-auto w-full animate-slide-in">
        <div className="flex justify-between items-center">
          <div className="font-bold leading-none text-xl capitalize">{assetType}s</div>
          <IconButton onClick={onClose}>
            <XMarkIcon className="w-7" />
          </IconButton>
        </div>

        <UploadButton
          onCompletedUpload={() => setRefreshKey((prev) => prev + 1)}
          assetType={assetType}
          className="mt-4 mb-7 w-full"
        >
          Upload
        </UploadButton>
        <div className="overflow-hidden">
          <div className="flex flex-wrap -m-2">
            {assets.map((asset, i) => (
              <div key={ids[i]} className="p-2 w-1/4">
                <div className="relative">
                  <div className="pt-[66%]" />
                  <div
                    data-testid={ASSET_CARD_TEST_ID}
                    onClick={() => {
                      onPick(asset);
                    }}
                    style={{ backgroundImage: `url(${asset.image_url})` }}
                    className={classNames(
                      "absolute bg-center bg-no-repeat bg-contain left-0 top-0 w-full h-full rounded-[10px] bg-gray-100 cursor-pointer"
                    )}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssetPicker;
